import React from 'react';
import PortalMark from './PortalMark';

/**
 * Loading boundary. The spinning portal — the mark — is the loading motion
 * (docs/plans/2026-09-portal-logo/final): it turns always, so a request that
 * outlasts the route transition shows the brand turning rather than a skeleton.
 * No fixed white layer covering the transition. Small in-button operations keep a
 * compact text status, never a mark.
 */
export default function LoadingSpinner({
  size = 'md',
  label,
  fullScreen = false,
  className = '',
}) {
  const compact = size === 'sm' && !fullScreen;
  return <div role="status" aria-busy="true" data-pr-loading={compact ? undefined : ''}
    className={`${fullScreen ? 'min-h-screen' : ''} ${compact ? 'inline-flex' : 'flex min-h-24 items-center justify-center'} ${className}`}>
    <span className="sr-only">{label || 'Loading…'}</span>
    {compact ? <span aria-hidden="true">…</span> : <span aria-hidden="true" className="block p-3"><PortalMark size={48} /></span>}
  </div>;
}

/**
 * Inline loading dots
 */
export function LoadingDots({ className = '' }) {
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <span className="w-2 h-2 bg-black animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 bg-black animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 bg-black animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

/**
 * Skeleton loading placeholder
 */
export function Skeleton({
  width,
  height,
  className = '',
}) {
  return (
    <div
      className={`bg-gray-200 animate-pulse ${className}`}
      style={{ width, height }}
    />
  );
}

/**
 * Card skeleton for loading states
 */
export function CardSkeleton({ className = '' }) {
  return (
    <div className={`border-2 border-gray-200 p-4 ${className}`}>
      <div className="flex gap-4">
        <Skeleton className="w-24 h-24 flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-2 w-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Row skeleton for tables/lists
 */
export function RowSkeleton({ className = '' }) {
  return (
    <div className={`border-2 border-gray-200 p-4 flex items-center gap-4 ${className}`}>
      <Skeleton className="w-12 h-12 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/4" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="w-20 h-8" />
        <Skeleton className="w-20 h-8" />
      </div>
    </div>
  );
}
